import type {
  ChartDetails,
  ChartDetailsColumn,
  AggregationColumn,
  AggregationFunction,
  QueryResultConfiguration,
  GroupDisplayConfig,
} from '@/models/dashboard.model'
import colors from 'vuetify/util/colors'

const COLOR_VARIANTS = ['base', 'darken2', 'lighten2', 'accent2'] as const

export const DEFAULT_COLORS = Object.entries(colors)
  .filter(([key]) => key !== 'shades')
  .flatMap(([, value]) => {
    const palette = value as Record<string, string>
    return COLOR_VARIANTS.map((variant) => palette[variant]).filter(Boolean)
  })

function stableColorIndex(key: string): number {
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

export function getDefaultColor(key: string): string {
  return DEFAULT_COLORS[stableColorIndex(key) % DEFAULT_COLORS.length]
}

export function getAggregationKey(agg: AggregationColumn): string {
  return `${agg.columnId}_${agg.aggregation}`
}

function getColumnIndex(rawData: ChartDetails, columnId: string): number {
  return rawData.columns.findIndex((col) => col.id === columnId)
}

function isNumericValue(value: string): boolean {
  if (value === '' || value === null || value === undefined) return false
  return !isNaN(parseFloat(value)) && isFinite(Number(value))
}

export function detectNumericColumns(rawData: ChartDetails): string[] {
  if (rawData.rows.length === 0) return []

  return rawData.columns
    .filter((col) => {
      const colIndex = getColumnIndex(rawData, col.id)
      let nonEmptyCount = 0

      for (const row of rawData.rows) {
        const value = row.cells[colIndex]?.value
        if (value !== '' && value !== null && value !== undefined) {
          nonEmptyCount++
          if (!isNumericValue(value)) return false
        }
      }

      return nonEmptyCount > 0
    })
    .map((col) => col.id)
}

export interface ResolvedGroupDisplay {
  originalName: string
  displayName: string
  color: string | undefined
}

export function resolveGroupDisplay(
  originalName: string,
  displayConfigs?: GroupDisplayConfig[],
  useDefaultColor = true,
): ResolvedGroupDisplay {
  const config = displayConfigs?.find((dc) => dc.originalName === originalName)
  return {
    originalName,
    displayName: config?.label ?? originalName,
    color: config?.color ?? (useDefaultColor ? getDefaultColor(originalName) : undefined),
  }
}

export function getDefaultConfiguration(
  rawData: ChartDetails,
  options?: { preAggregated?: boolean },
): QueryResultConfiguration {
  const numericColumns = detectNumericColumns(rawData)
  const allColumns = rawData.columns.map((col) => col.id)
  const nonNumericColumns = allColumns.filter((id) => !numericColumns.includes(id))

  const groupByColumnId = nonNumericColumns[0] ?? allColumns[0] ?? undefined

  const useSum = options?.preAggregated && numericColumns.length > 0
  const aggregationColumnId = useSum ? numericColumns[0] : (groupByColumnId ?? allColumns[0])
  const aggregation: AggregationFunction = useSum ? 'sum' : 'count'
  const label = useSum ? 'Sum' : 'Count'

  return {
    groupByColumnId,
    aggregations: aggregationColumnId
      ? [{ columnId: aggregationColumnId, aggregation, label }]
      : [],
  }
}

export function getAvailableColumnsForAggregation(
  rawData: ChartDetails,
  aggregation: AggregationFunction,
): ChartDetailsColumn[] {
  const needsNumeric: AggregationFunction[] = ['sum', 'average', 'min', 'max']

  if (needsNumeric.includes(aggregation)) {
    const numericColumnIds = detectNumericColumns(rawData)
    return rawData.columns.filter((col) => numericColumnIds.includes(col.id))
  }

  return rawData.columns
}
